import { Box, Typography, Link as MUILink, Stack, Chip } from '@mui/material';
import { useState } from 'react';
import { useGetAvailableIntegration, useGetAvailableTools } from 'apis/agency/integrations/hook';
import Button from 'components/Common/Button';
import DocumentationDrawer from 'components/Common/DocumentationDrawer';
import IntegrationSkeleton from './IntegrationSkeleton';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import GoBackButtonIcon from '@/assets/icons/GoBackButtonIcon';
import DefaultTemplate from './DefaultTemplate';
import { FiCheckCircle } from 'react-icons/fi';
import { titleCase } from '@/utils/helperFunctions';

export default function Integration() {
  const navigate = useNavigate();
  const [docsDrawerOpen, setDocsDrawerOpen] = useState(false);

  const { integrationId } = useParams<{ integrationId: string }>();
  const { data: integration, isLoading } = useGetAvailableIntegration();
  const { data: tools } = useGetAvailableTools(integrationId!);

  const articleId = integration?.article_id ?? null;

  if (isLoading) {
    return <IntegrationSkeleton />;
  }

  return (
    <>
      <Typography
        sx={{ cursor: 'pointer', alignItems: 'center', display: 'flex', gap: 1 }}
        mb={2}
        onClick={() => navigate(-1)}
      >
        <GoBackButtonIcon /> Back to All apps
      </Typography>
      <Box bgcolor='white' p={3} borderRadius={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          justifyContent='space-between'
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
        >
          <Stack direction='row' spacing={2} alignItems='flex-start' flex={1} minWidth={0}>
            <Box
              sx={{
                width: 100,
                height: 100,
                flexShrink: 0,
                borderRadius: 2,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
            >
              {integration?.avatar_url ? (
                <Box
                  component='img'
                  src={integration.avatar_url}
                  alt={integration?.name ?? ''}
                  sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }}
                />
              ) : null}
            </Box>
            <Stack spacing={1} flex={1} minWidth={0}>
              {integration?.name ? (
                <Typography variant='h6' fontWeight={600}>
                  {integration.name}
                </Typography>
              ) : null}
              <Box
                sx={{
                  typography: 'body2',
                  color: 'text.secondary',
                  '& p': { margin: 0 },
                  '& p + p': { mt: 1 }
                }}
              >
                <ReactMarkdown
                  components={{
                    a: ({ ...props }) => (
                      <MUILink
                        {...(props as any)}
                        component='a'
                        target='_blank'
                        rel='noopener'
                        sx={{ color: 'primary.main', fontWeight: 500 }}
                      />
                    )
                  }}
                >
                  {integration?.description}
                </ReactMarkdown>
              </Box>
            </Stack>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            flexShrink={0}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            <Button
              variant='outlined'
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              disabled={!articleId}
              onClick={() => setDocsDrawerOpen(true)}
            >
              View Documentation
            </Button>
            <Box
              component={Link}
              to='configure'
              sx={{
                textDecoration: 'none',
                width: { xs: '100%', sm: 'auto' },
                display: 'flex'
              }}
            >
              <Button sx={{ width: { xs: '100%', sm: 'auto' } }}>Configure</Button>
            </Box>
          </Stack>
        </Stack>
        <Box my={4}>
          {!!tools?.length && (
            <Typography variant='subtitle1' fontWeight={600} mb={2}>
              Available Tools
            </Typography>
          )}
          <Stack direction='row' flexWrap='wrap' gap={1.5}>
            {tools?.map((tool) => (
              <Chip
                key={tool.name}
                label={titleCase(tool.name.replace(/_/g, ' '))}
                icon={<FiCheckCircle size={16} />}
                sx={{
                  borderRadius: '20px',
                  border: '1px solid #ECECEC',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  fontWeight: 500,
                  px: 1,
                  '& .MuiChip-icon': {
                    color: '#22C55E'
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
        <DefaultTemplate integration={integration?.code} />
      </Box>
      <DocumentationDrawer
        open={docsDrawerOpen}
        onClose={() => setDocsDrawerOpen(false)}
        articleId={articleId}
        fallbackTitle={integration?.name ?? 'Documentation'}
      />
      <Outlet />
    </>
  );
}
